import { cn } from '@workspace/ui/lib/utils';
import * as icons from '../icon/assets';
import { Icon } from '../icon/icon';

export type TagColor = 'gray' | 'primary' | 'yellow' | 'green' | 'red' | 'black' | 'transparent';
export type TagSize = 'M-24' | 'S-16';
export interface TagProps {
  type?: 'default' | 'addNew' | 'closeable';
  color: TagColor;
  size: TagSize;
  text: string;
  icon?: keyof typeof icons;
  canChange?: boolean;
  rounded?: boolean;
  onClick?: () => void;
  onClose?: () => void;
}

const tagSizeMap = {
  'M-24': 24,
  'S-16': 16,
};

const colorMap = {
  gray: {
    backgroundColor: 'var(--surface-surface-gray-default)',
    color: 'var(--text-text-subdued)',
  },
  primary: {
    backgroundColor: 'var(--surface-surface-blue-tint)',
    color: 'var(--text-text-blue)',
  },
  yellow: {
    backgroundColor: 'var(--surface-surface-yellow-tint)',
    color: 'var(--text-text-yellow)',
  },
  green: {
    backgroundColor: 'var(--surface-surface-green-tint)',
    color: 'var(--text-text-green)',
  },
  red: {
    backgroundColor: 'var(--surface-surface-red-tint)',
    color: 'var(--text-text-red)',
  },
  black: {
    backgroundColor: 'var(--surface-surface-black)',
    color: 'var(--text-text-on-color)',
  },
  transparent: {
    backgroundColor: 'transparent',
    color: 'var(--text-text-default)',
  },
};

export function Tag({
  type = 'default',
  color,
  size,
  text,
  icon,
  canChange,
  rounded = false,
  onClick,
  onClose,
}: TagProps) {
  const isKorean = /^[가-힣ㄱ-ㅎㅏ-ㅣ ]+$/.test(text);

  if (type === 'closeable') {
    return (
      <span
        className={cn(
          'flex items-center gap-1 w-fit font-bold leading-font-height-6 rounded-sm',
          size === 'M-24' && 'px-2 py-1 text-size-6',
          size === 'S-16' && 'px-1 py-0.25 text-size-7',
          rounded && 'rounded-[var(--tag-rounded)]',
        )}
        style={{
          backgroundColor: colorMap[color].backgroundColor,
          color: colorMap[color].color,
        }}
      >
        {text}
        <Icon name='Close' size={16} color='var(--icon-icon-subdued)' onClick={onClose} />
      </span>
    );
  }

  if (type === 'addNew') {
    return (
      <span
        onClick={onClick}
        className={cn(
          'flex items-center gap-1 w-fit font-bold leading-font-height-6 rounded-sm border border-dashed border-[var(--border-border-default)] bg-white',
          size === 'M-24' && 'px-2 py-1 text-size-6',
          size === 'S-16' && 'px-1 py-0.25 text-size-7',
          rounded && 'rounded-[var(--tag-rounded)]',
        )}
        style={{
          backgroundColor: '#fff',
          color: '#595959',
        }}
      >
        <Icon name='Plus' size={16} color='#262626' />
        {text}
      </span>
    );
  }

  return (
    <span
      onClick={canChange ? onClick : undefined}
      className={cn(
        'flex items-center gap-1 w-fit font-bold leading-font-height-6 rounded-sm',
        size === 'M-24' && 'px-2 py-1 text-size-6',
        size === 'S-16' && 'px-1 py-0.25 text-size-7',
        rounded && 'rounded-[var(--tag-rounded)]',
      )}
      style={{
        backgroundColor: colorMap[color].backgroundColor,
        color: colorMap[color].color,
      }}
    >
      {icon && <Icon name={icon} size={tagSizeMap[size]} color={colorMap[color].color} />}
      {text}
      {canChange && <Icon name='Down' size={12} color={colorMap[color].color} />}
    </span>
  );
}
